import React, { useState, useEffect, useRef } from 'react';

const SearchableDropdown = ({
  items = [],
  onSelect,
  placeholder = "Select an item",
  renderItem = (item) => item?.toString() || '',
  defaultValue = null,
  disabled = false
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Sync selectedItem with defaultValue when it changes (e.g., after async fetch)
  useEffect(() => {
    if (!defaultValue || !items) {
      setSelectedItem(null);
      setInputValue('');
      return;
    }

    // If defaultValue is already an object that exists in items, use it
    if (typeof defaultValue === 'object' && items.includes(defaultValue)) {
      setSelectedItem(defaultValue);
    } else {
      // Try to find the item in the items array that matches the defaultValue
      // Matches either the item itself (if it's a primitive) or by its rendered value or id
      const item = items.find(i =>
        i === defaultValue ||
        renderItem(i) === defaultValue ||
        (i?.id?.toString() === defaultValue?.toString())
      );
      setSelectedItem(item || null);
    }
  }, [defaultValue, items, renderItem]);

  useEffect(() => {
    setInputValue(selectedItem ? renderItem(selectedItem) : '');
  }, [selectedItem, renderItem]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setInputValue(selectedItem ? renderItem(selectedItem) : '');
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [selectedItem, renderItem]);

  // Filter items based on current field value
  const filteredItems = items?.filter(item => {
    if (!inputValue?.trim()) return true;
    return renderItem(item)?.toLowerCase()?.includes(inputValue.toLowerCase());
  });

  const handleSelect = (item) => {
    setSelectedItem(item);
    setInputValue(renderItem(item));
    onSelect?.(item);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full h-[32px] rounded px-3 bg-white border border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-[14px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onFocus={() => !disabled && setIsOpen(true)}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
      />

      {isOpen && !disabled && (
        <div className="absolute z-[1000] mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-[240px] overflow-auto">
          {filteredItems?.length > 0 ? (
            filteredItems.map((item, index) => (
              <button
                key={item?.id ?? `${renderItem(item)}-${index}`}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-[14px]"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
              >
                {renderItem(item)}
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-gray-500 text-[13px]">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
